import React, { useState } from "react";
import { Search, Filter, Briefcase, Clock } from "lucide-react";
import Navbar from "../layout/Navbar";
import career from "../assets/career.png";
import Footer from "../layout/Footer";

const Career = () => {
  // Expanded job data with more details
  const allJobs = [
    {
      id: 1,
      position: "Doctor",
      department: "Health Care",
      experience: "1-2 Years",
      location: "Trauma Centre IMS",
      type: "Full-Time",
      description:
        "Join our team of dedicated doctors to provide exceptional patient care in a state-of-the-art facility.",
    },
    {
      id: 2,
      position: "Nursing",
      department: "Health Care",
      experience: "1-2 Years",
      location: "Main Hospital",
      type: "Part-Time",
      description:
        "Provide compassionate nursing care to patients in various departments.",
    },
    {
      id: 3,
      position: "Customer Care Executive",
      department: "Others",
      experience: "1-2 Years",
      location: "City Medical Center",
      type: "Full-Time",
      description:
        "Assist patients with inquiries and ensure a positive experience at our facilities.",
    },
    {
      id: 4,
      position: "Associate Purchase",
      department: "Others",
      experience: "1-2 Years",
      location: "Trauma Centre IMS",
      type: "Full-Time",
      description:
        "Manage procurement and inventory for medical supplies and equipment.",
    },
  ];

  // State for filters and search
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("All");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  // Filter jobs based on search and department
  const filteredJobs = allJobs.filter((job) => {
    const matchesSearch = job.position
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesDepartment =
      departmentFilter === "All" || job.department === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  // Group jobs by department for display
  const healthCareJobs = filteredJobs.filter(
    (job) => job.department === "Health Care"
  );
  const otherJobs = filteredJobs.filter((job) => job.department === "Others");

  // Open modal with job details
  const openModal = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  // Job Card Component
  const JobCard = ({ job }) => (
    <div className="bg-white rounded-xl shadow-md p-6 transition-all hover:shadow-lg hover:border-teal-100 border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-800">{job.position}</h3>
      <p className="text-sm text-gray-600 mt-1">{job.description}</p>
      <div className="grid grid-cols-2 gap-4 mt-4 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-teal-500" />
          <span>{job.department}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-teal-500" />
          <span>{job.experience}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-teal-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span>{job.location}</span>
        </div>
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-teal-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{job.type}</span>
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <button
          onClick={() => openModal(job)}
          className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg hover:opacity-90 transition"
          aria-label={`View details for ${job.position}`}
        >
          View Details
        </button>
      </div>
    </div>
  );

  // Job Modal Component
  const JobModal = ({ job, onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
        <h3 className="text-xl font-semibold text-gray-800">{job.position}</h3>
        <p className="text-sm text-gray-600 mt-2">{job.description}</p>
        <div className="grid grid-cols-2 gap-4 mt-4 text-sm text-gray-600">
          <div>
            <span className="font-medium">Department:</span> {job.department}
          </div>
          <div>
            <span className="font-medium">Experience:</span> {job.experience}
          </div>
          <div>
            <span className="font-medium">Location:</span> {job.location}
          </div>
          <div>
            <span className="font-medium">Type:</span> {job.type}
          </div>
        </div>
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-800">Apply Now</h4>
          <form className="mt-4 space-y-4">
            <input
              type="text"
              placeholder="Full Name"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none"
            />
            <input
              type="email"
              placeholder="Email"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none"
            />
            <input
              type="file"
              className="w-full px-4 py-2 border rounded-lg"
              aria-label="Upload resume"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm text-white bg-teal-500 rounded-lg hover:bg-teal-600"
              >
                Submit Application
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <div
        className="relative h-64 md:h-80 bg-cover bg-center shadow-md"
        style={{ backgroundImage: `url(${career})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/70 to-blue-900/70" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 text-white text-center">
          <h1 className="text-3xl md:text-4xl font-bold drop-shadow-md">
            Join Our Team
          </h1>
          <p className="mt-2 text-sm md:text-lg max-w-xl">
            Explore exciting career opportunities and make a difference in
            healthcare.
          </p>
          <div className="flex items-center mt-4 space-x-2 text-sm font-medium">
            {["Home", "Contact", "Career"].map((step, index, arr) => (
              <div key={step} className="flex items-center">
                <span
                  className={`${
                    step === "Career" ? "font-semibold" : "opacity-70"
                  } hover:underline cursor-pointer`}
                >
                  {step}
                </span>
                {index < arr.length - 1 && (
                  <span className="mx-2 opacity-60">/</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Job Vacancy Section */}
      <div className="bg-[#e6f6f6] py-10 px-4 sm:px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-800">
            Job Vacancies
          </h2>
          <p className="mt-2 text-center text-gray-600 max-w-2xl mx-auto">
            Discover roles that align with your skills and passion for healthcare.
          </p>

          {/* Search and Filters */}
          <div className="mt-8 flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by position"
                className="w-full px-4 py-3 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:outline-none"
              />
              <Search className="absolute w-5 h-5 text-gray-400 right-4 top-1/2 -translate-y-1/2" />
            </div>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="px-4 py-3 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:outline-none"
            >
              <option value="All">All Departments</option>
              <option value="Health Care">Health Care</option>
              <option value="Others">Others</option>
            </select>
          </div>

          {/* Job Listings */}
          {filteredJobs.length > 0 ? (
            <>
              {healthCareJobs.length > 0 && (
                <div className="mt-10">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Health Care{" "}
                    <span className="text-purple-500">
                      ({healthCareJobs.length})
                    </span>
                  </h3>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {healthCareJobs.map((job) => (
                      <JobCard key={job.id} job={job} />
                    ))}
                  </div>
                </div>
              )}
              {otherJobs.length > 0 && (
                <div className="mt-10">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Others{" "}
                    <span className="text-purple-500">
                      ({otherJobs.length})
                    </span>
                  </h3>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {otherJobs.map((job) => (
                      <JobCard key={job.id} job={job} />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Search className="w-12 h-12 text-teal-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-800">
                No jobs found
              </h3>
              <p className="text-sm text-gray-600">
                Try adjusting your search or filters.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Job Details */}
      {isModalOpen && selectedJob && (
        <JobModal job={selectedJob} onClose={closeModal} />
      )}

      <Footer />
    </div>
  );
};

export default Career;